<template>
    <div class="card card-body shadow-sm mt-0">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ route.params.id ? 'Editar Bloco de Questão' : 'Novo Bloco de Questão' }}
        </h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
  
      <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarBloco" @cancel="voltar">
        <!-- Dados do Bloco de Avaliação -->
        <FormSection title="Informações do Bloco">
          <!-- Alinha os campos horizontalmente -->
          <div class="d-flex gap-3 flex-wrap">
            <!-- SelectField para Categoria de Avaliação -->
            <SelectField
              v-model="bloco.id_categoria"
              label="Categoria de Avaliação <span class='required'>*</span>"
              id="id_categoria"
              :options="categoriasOptions"
              :class="{ 'error-input': errors.id_categoria }"
              required
              width="400px"
            />
  
            <!-- InputField para Bloco de Avaliação -->
            <InputField
              v-model="bloco.bloco"
              label="Bloco de Avaliação <span class='required'>*</span>"
              id="bloco"
              placeholder="Digite o nome do bloco de avaliação"
              required
              :class="{ 'error-input': errors.bloco }"
              width="400px"
            />
          </div>
        </FormSection>
      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import InputField from "@/components/Layout/InputField.vue";
  import SelectField from "@/components/Layout/SelectField.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import axios from "@/services/axios";
  
  export default {
    components: { InputField, SelectField, FormSection, BaseForm },
    setup() {
      const route = useRoute();
      const router = useRouter();
  
      const bloco = reactive({
        id: null,
        id_categoria: "",
        bloco: "",
      });
  
      const categorias = ref([]);
      const errors = reactive({});
      const formTouched = ref(false);
      const isSubmitting = ref(false);
  
      const validateForm = () => {
        errors.id_categoria = bloco.id_categoria ? "" : "A categoria é obrigatória.";
        errors.bloco = bloco.bloco.trim() ? "" : "O nome do bloco é obrigatório.";
      };
  
      const isFormValid = computed(() => {
        return !!(
            bloco.id_categoria &&
            bloco.bloco.trim() !== "" &&
            !Object.values(errors).some((error) => error)
        );
      });
  
      const categoriasOptions = computed(() => {
        return categorias.value.map((cat) => ({
          value: cat.id,
          text: cat.categoria,
        }));
      });
  
      const novoBloco = () => {
        formTouched.value = false;
        isSubmitting.value = false;
        Object.assign(bloco, {
          id_categoria: "",
          bloco: "",
        });
        for (const key in errors) {
          if (Object.hasOwn(errors, key)) errors[key] = "";
        }
      };
  
      const salvarBloco = async () => {
        if (isSubmitting.value) return;
  
        validateForm();
  
        if (!isFormValid.value) {
          console.error("O formulário contém erros:", errors);
          return;
        }
  
        isSubmitting.value = true;
        try {
          const method = bloco.id ? "put" : "post";
          const url = bloco.id
            ? `/configuracoes/parametros-avaliacoes/blocos/${bloco.id}`
            : "/configuracoes/parametros-avaliacoes/blocos";
  
          await axios[method](url, {
            id_categoria: bloco.id_categoria,
            bloco: bloco.bloco,
          });
  
          router.push("/configuracoes/parametros-avaliacoes/blocos");
        } catch (error) {
          console.error("Erro ao salvar bloco:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/configuracoes/parametros-avaliacoes/blocos");
      };
  
      const carregarCategorias = async () => {
        try {
          // 1ª tentativa: sem paginação
          let resp;
          try {
            resp = await axios.get('/configuracoes/parametros-avaliacoes/categorias', {
              params: { limit: 'all', sortField: 'nome', sortOrder: 'asc' }
            });
          } catch {
            // Fallback: paginação "alta"
            resp = await axios.get('/configuracoes/parametros-avaliacoes/categorias', {
              params: { page: 1, limit: 10000, sortField: 'nome', sortOrder: 'asc' }
            });
          }

          const lista = Array.isArray(resp?.data?.data)
            ? resp.data.data
            : (Array.isArray(resp?.data) ? resp.data : []);

          const somenteAtivas = (lista || []).filter(c => c.ativo !== false);

          somenteAtivas.sort((a, b) =>
            String(a.nome ?? '').localeCompare(String(b.nome ?? ''), 'pt-BR', {
              sensitivity: 'base',
              numeric: true
            })
          );

          categorias.value = somenteAtivas; // mantém o mesmo formato que você já usa
        } catch (error) {
          console.error('Erro ao carregar categorias:', error);
          categorias.value = [];
        }
      };
  
      onMounted(async () => {
        await carregarCategorias();
        const blocoId = route.params.id;
        if (blocoId) {
          try {
            const response = await axios.get(`/configuracoes/parametros-avaliacoes/blocos/${blocoId}`);
            Object.assign(bloco, response.data);
          } catch (error) {
            console.error("Erro ao carregar o bloco:", error);
          }
        } else {
          novoBloco();
        }
      });
  
      return {
        bloco,
        categorias,
        categoriasOptions,
        errors,
        isFormValid,
        formTouched,
        isSubmitting,
        salvarBloco,
        voltar,
        route,
      };
    },
  };
  </script>
  
  <style scoped>
  .card {
    max-width: 90%;
    margin: 0 auto;
  }
  
  .error-input {
    border-color: #dc3545;
  }
  
  .error-message {
    color: #dc3545;
    font-size: 0.875rem;
  }
  
  /* Opcional: Ajustes para garantir alinhamento horizontal consistente */
  .d-flex {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
  </style>
  